(() => {
  /**
   * Общий механизм появления при скролле: элементы с [data-scroll-reveal]
   * получают класс scroll-reveal--in, когда попадают во вьюпорт; попавшие
   * одновременно - с нарастающей задержкой через --scroll-reveal-delay.
   * Что именно и на каких брейкпоинтах анимируется, решает CSS секции:
   * без соответствующих правил класс ничего не меняет. Гейты по html.js
   * и prefers-reduced-motion живут там же, в CSS.
   *
   * Очередь подстраивается под скорость скролла: при спокойном чтении
   * карточки едут по одной, при быстрой прокрутке шаг сжимается до нуля,
   * иначе экран остаётся белым, пока очередь догоняет.
   */
  const REVEALED_CLASS = 'scroll-reveal--in';
  const DELAY_PROPERTY = '--scroll-reveal-delay';
  /**
   * Пауза между элементами, попавшими во вьюпорт одновременно при медленном
   * скролле: следующий стартует, когда предыдущий почти замер (полёт 0.7s).
   */
  const STAGGER_MS = 540;
  /** Какая доля элемента должна показаться, чтобы он поехал. */
  const VISIBLE_THRESHOLD = 0.2;
  /**
   * Скорость (px/s), при которой очередь схлопывается полностью. Чтение
   * идёт на 200-600 px/s, флик на телефоне даёт 1500-4000.
   */
  const FAST_SCROLL_PX_PER_SECOND = 1000;
  /** Без scroll-событий дольше этого считаем, что страница стоит. */
  const SPEED_STALE_MS = 120;
  const MS_PER_SECOND = 1000;

  const targets = document.querySelectorAll('[data-scroll-reveal]');
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((target) => target.classList.add(REVEALED_CLASS));
    return;
  }

  /*
    Скорость меряем по нативным scroll-событиям: Lenis тоже двигает страницу
    через window.scrollTo, так что события приходят и при плавном скролле.
  */
  let lastScrollY = window.scrollY;
  let lastScrollAt = performance.now();
  let speed = 0;

  /** @returns {number} 0 - страница стоит или ползёт, 1 - быстрый скролл */
  const speedRatio = (now) => {
    if (now - lastScrollAt > SPEED_STALE_MS) return 0;
    return Math.min(1, speed / FAST_SCROLL_PX_PER_SECOND);
  };

  /*
    Очередь по времени, а не индекс в батче: соседние карточки при плавном
    скролле пересекают порог в разных кадрах наблюдателя, и покадровые батчи
    ломали бы строгую очерёдность. Каждый reveal бронирует слот; следующий
    стартует не раньше, чем через шаг после предыдущего. Простой прошёл -
    очередь пуста и элемент едет сразу.
  */
  let nextSlotAt = 0;
  /** Элементы, чей переход ещё ждёт своей задержки: target -> время старта */
  const pending = new Map();

  const reveal = (target) => {
    // Слот занимают только элементы, которые CSS реально анимирует на текущем
    // брейкпоинте: контейнер и карточки размечены одновременно, и неанимируемый
    // контейнер не должен задерживать очередь
    if (parseFloat(getComputedStyle(target).transitionDuration) > 0) {
      const now = performance.now();
      const calm = 1 - speedRatio(now);
      const wait = Math.max(0, nextSlotAt - now) * calm;
      const startAt = now + wait;
      target.style.setProperty(DELAY_PROPERTY, `${Math.round(wait)}ms`);
      nextSlotAt = startAt + STAGGER_MS * calm;
      if (wait > 0) pending.set(target, startAt);
    }
    target.classList.add(REVEALED_CLASS);
  };

  /*
    Уже запланированные переходы CSS ускорить нельзя: их delay зафиксирован
    на старте. Пока идёт задержка, элемент стоит в исходном состоянии, поэтому
    снять класс и повесить заново с нулевой задержкой визуально бесшовно.
  */
  const flushPending = (now) => {
    pending.forEach((startAt, target) => {
      pending.delete(target);
      if (startAt <= now) return;
      target.classList.remove(REVEALED_CLASS);
      void target.offsetWidth;
      target.style.setProperty(DELAY_PROPERTY, '0ms');
      target.classList.add(REVEALED_CLASS);
    });
    nextSlotAt = 0;
  };

  window.addEventListener(
    'scroll',
    () => {
      const now = performance.now();
      const elapsed = now - lastScrollAt;
      if (elapsed <= 0) return;
      speed = (Math.abs(window.scrollY - lastScrollY) / elapsed) * MS_PER_SECOND;
      lastScrollY = window.scrollY;
      lastScrollAt = now;
      if (speedRatio(now) >= 1) flushPending(now);
    },
    { passive: true }
  );

  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .forEach((entry) => {
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
    },
    { threshold: VISIBLE_THRESHOLD }
  );

  targets.forEach((target) => observer.observe(target));
})();
